/**
 * Demo dataset for ETrax interactive demo mode.
 *
 * This module fabricates a realistic, self-contained dataset for a fictional
 * athletics department ("Riverside High School — Rapids"). It is consumed by the
 * demo request router (./server.ts) when the app runs in demo mode, so that the
 * entire product can be explored with zero backend / database.
 *
 * Equipment objects intentionally carry a SUPERSET of fields:
 *  - the shared-type fields used by the Equipment list + detail pages
 *    (status as the GREEN/YELLOW/RED/RETIRED/LOST health enum, categoryId, etc.)
 *  - flattened convenience fields used by pages that call `fetch('/api/equipment')`
 *    directly (Scan / Voice / Reports): `category`, `location`, `condition`,
 *    `cost`, `checkedOutTo`.
 * Checkout state is tracked independently of health status via `checkedOutTo`,
 * which is the correct model (the health enum has no "checked out" value).
 */

const now = () => new Date();
const daysAgo = (n: number): string => {
  const d = now();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};
const daysFromNow = (n: number): string => daysAgo(-n);

export const DEMO_ORG = {
  id: 'org_riverside',
  name: 'Riverside Unified School District',
  type: 'DISTRICT',
  subdomain: 'riverside',
  isActive: true,
};

export const DEMO_SCHOOL = {
  id: 'school_rhs',
  organizationId: DEMO_ORG.id,
  name: 'Riverside High School',
  code: 'RHS',
  city: 'Riverside',
  state: 'CA',
  mascot: 'Rapids',
  colors: ['#0b2a4a', '#f5b301'],
  qrCodePrefix: 'RHS',
  defaultStatus: 'GREEN',
};

export const DEMO_USER = {
  id: 'user_taylor',
  email: 'coach.taylor@riverside.edu',
  firstName: 'Jordan',
  lastName: 'Taylor',
  name: 'Jordan Taylor',
  role: 'SUPER_ADMIN', // surfaces the admin nav (Auth Config / User Management) in the demo
  permissions: ['*'],
  schoolId: DEMO_SCHOOL.id,
  organizationId: DEMO_ORG.id,
  isActive: true,
  emailVerified: true,
  avatar: undefined,
};

export const DEMO_STAFF = [
  DEMO_USER,
  { id: 'user_martinez', email: 'a.martinez@riverside.edu', firstName: 'Alex', lastName: 'Martinez', role: 'COACH', schoolId: DEMO_SCHOOL.id, organizationId: DEMO_ORG.id, isActive: true, emailVerified: true },
  { id: 'user_lee', email: 'c.lee@riverside.edu', firstName: 'Casey', lastName: 'Lee', role: 'COACH', schoolId: DEMO_SCHOOL.id, organizationId: DEMO_ORG.id, isActive: true, emailVerified: true },
  { id: 'user_rivera', email: 's.rivera@riverside.edu', firstName: 'Sam', lastName: 'Rivera', role: 'EQUIPMENT_MANAGER', schoolId: DEMO_SCHOOL.id, organizationId: DEMO_ORG.id, isActive: true, emailVerified: true },
  { id: 'user_cole', email: 'd.cole@riverside.edu', firstName: 'Dana', lastName: 'Cole', role: 'SCHOOL_ADMIN', schoolId: DEMO_SCHOOL.id, organizationId: DEMO_ORG.id, isActive: false, emailVerified: true },
];

// Athletes / students that equipment gets checked out to.
const ATHLETES = [
  'Marcus Bell', 'Tyrese Johnson', 'Olivia Nguyen', 'Diego Ramirez', 'Hannah Schmidt',
  'Jaylen Brooks', 'Mia Castillo', 'Ethan Park', 'Sofia Rossi', 'Noah Williams',
];

interface CategoryDef {
  id: string;
  name: string;
  icon: string;
  color: string;
  sport: string;
}

export const DEMO_CATEGORIES_DEF: CategoryDef[] = [
  { id: 'cat_basketball', name: 'Basketball', icon: '🏀', color: '#f97316', sport: 'Basketball' },
  { id: 'cat_football', name: 'Football', icon: '🏈', color: '#7c3aed', sport: 'Football' },
  { id: 'cat_soccer', name: 'Soccer', icon: '⚽', color: '#16a34a', sport: 'Soccer' },
  { id: 'cat_volleyball', name: 'Volleyball', icon: '🏐', color: '#dc2626', sport: 'Volleyball' },
  { id: 'cat_baseball', name: 'Baseball', icon: '⚾', color: '#2563eb', sport: 'Baseball' },
  { id: 'cat_track', name: 'Track & Field', icon: '🏃', color: '#0891b2', sport: 'Track & Field' },
  { id: 'cat_weight', name: 'Weight Room', icon: '🏋️', color: '#4b5563', sport: 'Strength & Conditioning' },
  { id: 'cat_medical', name: 'Training & Medical', icon: '🩹', color: '#db2777', sport: 'Athletic Training' },
  { id: 'cat_uniforms', name: 'Uniforms', icon: '👕', color: '#ca8a04', sport: 'All Sports' },
];

interface LocationDef {
  id: string;
  name: string;
  type: string;
  building?: string;
  isSecure?: boolean;
}

export const DEMO_LOCATIONS_DEF: LocationDef[] = [
  { id: 'loc_main_gym', name: 'Main Gym', type: 'GYM', building: 'Athletic Center' },
  { id: 'loc_aux_gym', name: 'Auxiliary Gym', type: 'GYM', building: 'Athletic Center' },
  { id: 'loc_cage', name: 'Equipment Cage', type: 'STORAGE', building: 'Athletic Center', isSecure: true },
  { id: 'loc_fieldhouse', name: 'Field House', type: 'BUILDING', building: 'Stadium' },
  { id: 'loc_weight', name: 'Weight Room', type: 'ROOM', building: 'Athletic Center' },
  { id: 'loc_trainer', name: "Athletic Trainer's Room", type: 'ROOM', building: 'Athletic Center' },
  { id: 'loc_outdoor', name: 'Outdoor Storage', type: 'STORAGE', building: 'Stadium' },
  { id: 'loc_stadium', name: 'Stadium Field', type: 'FIELD', building: 'Stadium' },
];

interface ItemTemplate {
  name: string;
  cat: string; // category id
  loc: string; // location id
  brand: string;
  model?: string;
  price: number;
  condition: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED';
  status?: 'GREEN' | 'YELLOW' | 'RED' | 'RETIRED' | 'LOST';
  size?: string;
  checkedOutTo?: string;
  dueInDays?: number; // negative => overdue
}

// 42 hand-tuned items spanning every category, with a believable spread of
// health, condition, and checkout state.
const TEMPLATES: ItemTemplate[] = [
  // Basketball
  { name: 'Game Basketball', cat: 'cat_basketball', loc: 'loc_main_gym', brand: 'Spalding', model: 'TF-1000', price: 79.99, condition: 'EXCELLENT' },
  { name: 'Practice Basketball', cat: 'cat_basketball', loc: 'loc_main_gym', brand: 'Wilson', model: 'Evolution', price: 64.99, condition: 'GOOD' },
  { name: 'Portable Hoop System', cat: 'cat_basketball', loc: 'loc_aux_gym', brand: 'Spalding', model: 'Arena Pro', price: 1299.0, condition: 'GOOD', status: 'YELLOW' },
  { name: 'Shooting Machine', cat: 'cat_basketball', loc: 'loc_main_gym', brand: 'Dr. Dish', model: 'All-Star', price: 8995.0, condition: 'EXCELLENT', checkedOutTo: 'Coach Martinez', dueInDays: 3 },
  { name: 'Scoreboard Controller', cat: 'cat_basketball', loc: 'loc_cage', brand: 'Daktronics', price: 449.0, condition: 'FAIR', status: 'YELLOW' },
  // Football
  { name: 'Varsity Helmet (L)', cat: 'cat_football', loc: 'loc_fieldhouse', brand: 'Riddell', model: 'SpeedFlex', price: 379.99, condition: 'EXCELLENT', size: 'L', checkedOutTo: 'Marcus Bell', dueInDays: 21 },
  { name: 'Varsity Helmet (M)', cat: 'cat_football', loc: 'loc_fieldhouse', brand: 'Schutt', model: 'F7', price: 359.99, condition: 'GOOD', size: 'M', checkedOutTo: 'Tyrese Johnson', dueInDays: 21 },
  { name: 'Shoulder Pads (L)', cat: 'cat_football', loc: 'loc_fieldhouse', brand: 'Riddell', model: 'Power SPK+', price: 289.99, condition: 'GOOD', size: 'L' },
  { name: 'Blocking Sled', cat: 'cat_football', loc: 'loc_outdoor', brand: 'Rogers Athletic', price: 2499.0, condition: 'FAIR', status: 'YELLOW' },
  { name: 'Practice Football', cat: 'cat_football', loc: 'loc_cage', brand: 'Wilson', model: 'GST', price: 89.99, condition: 'GOOD' },
  { name: 'Tackling Dummy', cat: 'cat_football', loc: 'loc_outdoor', brand: 'Fisher', price: 219.0, condition: 'POOR', status: 'RED' },
  // Soccer
  { name: 'Match Soccer Ball', cat: 'cat_soccer', loc: 'loc_outdoor', brand: 'Nike', model: 'Flight', price: 159.99, condition: 'EXCELLENT' },
  { name: 'Training Soccer Ball', cat: 'cat_soccer', loc: 'loc_outdoor', brand: 'Adidas', model: 'Tiro', price: 34.99, condition: 'GOOD' },
  { name: 'Portable Goal (Pair)', cat: 'cat_soccer', loc: 'loc_stadium', brand: 'Kwik Goal', price: 899.0, condition: 'GOOD', status: 'YELLOW' },
  { name: 'Corner Flags Set', cat: 'cat_soccer', loc: 'loc_outdoor', brand: 'Champion', price: 49.99, condition: 'FAIR' },
  { name: 'Agility Ladder', cat: 'cat_soccer', loc: 'loc_cage', brand: 'SKLZ', price: 29.99, condition: 'GOOD', checkedOutTo: 'Olivia Nguyen', dueInDays: -2 },
  // Volleyball
  { name: 'Game Volleyball', cat: 'cat_volleyball', loc: 'loc_main_gym', brand: 'Molten', model: 'Flistatec', price: 69.99, condition: 'EXCELLENT' },
  { name: 'Net & Standards', cat: 'cat_volleyball', loc: 'loc_main_gym', brand: 'Tachikara', price: 549.0, condition: 'GOOD' },
  { name: 'Referee Stand', cat: 'cat_volleyball', loc: 'loc_cage', brand: 'Gared', price: 329.0, condition: 'FAIR', status: 'YELLOW' },
  { name: 'Ball Cart', cat: 'cat_volleyball', loc: 'loc_cage', brand: 'Champro', price: 119.0, condition: 'GOOD', checkedOutTo: 'Coach Lee', dueInDays: 5 },
  // Baseball
  { name: 'Batting Helmet', cat: 'cat_baseball', loc: 'loc_fieldhouse', brand: 'Rawlings', model: 'Mach', price: 49.99, condition: 'GOOD', size: 'M', checkedOutTo: 'Diego Ramirez', dueInDays: 14 },
  { name: 'Catchers Gear Set', cat: 'cat_baseball', loc: 'loc_fieldhouse', brand: 'Easton', model: 'Elite X', price: 299.99, condition: 'GOOD' },
  { name: 'Pitching Machine', cat: 'cat_baseball', loc: 'loc_outdoor', brand: 'JUGS', model: 'BP3', price: 1599.0, condition: 'EXCELLENT', checkedOutTo: 'Coach Martinez', dueInDays: 1 },
  { name: 'Aluminum Bat (-3)', cat: 'cat_baseball', loc: 'loc_cage', brand: 'Louisville Slugger', model: 'Meta', price: 349.99, condition: 'GOOD', size: '33"' },
  { name: 'L-Screen', cat: 'cat_baseball', loc: 'loc_outdoor', brand: 'BSN', price: 229.0, condition: 'POOR', status: 'RED' },
  { name: 'Field Drag Mat', cat: 'cat_baseball', loc: 'loc_outdoor', brand: 'Newstripe', price: 189.0, condition: 'FAIR', status: 'RETIRED' },
  // Track & Field
  { name: 'Starting Blocks', cat: 'cat_track', loc: 'loc_stadium', brand: 'Gill Athletics', price: 219.99, condition: 'GOOD' },
  { name: 'Hurdles (Set of 10)', cat: 'cat_track', loc: 'loc_outdoor', brand: 'Gill Athletics', price: 1099.0, condition: 'GOOD', status: 'YELLOW' },
  { name: 'Shot Put (12lb)', cat: 'cat_track', loc: 'loc_cage', brand: 'Nelco', price: 59.99, condition: 'EXCELLENT', checkedOutTo: 'Hannah Schmidt', dueInDays: 7 },
  { name: 'Relay Batons', cat: 'cat_track', loc: 'loc_cage', brand: 'Gill', price: 39.99, condition: 'GOOD' },
  { name: 'Stopwatch', cat: 'cat_track', loc: 'loc_cage', brand: 'Ultrak', price: 34.99, condition: 'FAIR', status: 'LOST' },
  // Weight Room
  { name: 'Olympic Barbell', cat: 'cat_weight', loc: 'loc_weight', brand: 'Rogue', model: 'Ohio', price: 295.0, condition: 'EXCELLENT' },
  { name: 'Bumper Plate Set', cat: 'cat_weight', loc: 'loc_weight', brand: 'Rogue', price: 645.0, condition: 'GOOD' },
  { name: 'Adjustable Bench', cat: 'cat_weight', loc: 'loc_weight', brand: 'Rep Fitness', price: 399.0, condition: 'GOOD' },
  { name: 'Kettlebell (24kg)', cat: 'cat_weight', loc: 'loc_weight', brand: 'Rogue', price: 89.0, condition: 'FAIR', status: 'YELLOW' },
  { name: 'Battle Rope', cat: 'cat_weight', loc: 'loc_weight', brand: 'Onnit', price: 79.0, condition: 'POOR', status: 'RED' },
  // Training & Medical
  { name: 'AED Defibrillator', cat: 'cat_medical', loc: 'loc_trainer', brand: 'Philips', model: 'HeartStart', price: 1399.0, condition: 'EXCELLENT' },
  { name: 'Med Kit (Sideline)', cat: 'cat_medical', loc: 'loc_trainer', brand: 'Cramer', price: 149.0, condition: 'GOOD', checkedOutTo: 'Coach Lee', dueInDays: -1 },
  { name: 'Ice Machine', cat: 'cat_medical', loc: 'loc_trainer', brand: 'Hoshizaki', price: 1899.0, condition: 'GOOD', status: 'YELLOW' },
  { name: 'Treatment Table', cat: 'cat_medical', loc: 'loc_trainer', brand: 'Hausmann', price: 749.0, condition: 'FAIR' },
  // Uniforms
  { name: 'Home Jersey Set (Basketball)', cat: 'cat_uniforms', loc: 'loc_cage', brand: 'Nike', price: 1450.0, condition: 'EXCELLENT' },
  { name: 'Away Jersey Set (Football)', cat: 'cat_uniforms', loc: 'loc_cage', brand: 'Under Armour', price: 2890.0, condition: 'GOOD', checkedOutTo: 'Sam Rivera', dueInDays: 30 },
];

export interface DemoEquipment {
  id: string;
  schoolId: string;
  name: string;
  description: string;
  brand: string;
  model?: string;
  serialNumber: string;
  qrCode: string;
  categoryId: string;
  category: string; // flattened name (Scan/Voice/Reports)
  locationId: string;
  location: string; // flattened name
  status: string; // health enum: GREEN/YELLOW/RED/RETIRED/LOST
  condition: string;
  size?: string;
  purchasePrice: number;
  cost: string; // flattened numeric string for Reports (no currency symbol — the UI adds it)
  purchaseDate: string;
  warrantyExpiry?: string;
  lastInspection?: string;
  nextInspection?: string;
  assignedTo?: string;
  checkedOutTo?: string;
  checkedOutDate?: string;
  dueDate?: string;
  notes: string;
  images: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

function pad(n: number): string {
  return String(n).padStart(4, '0');
}

function buildEquipment(): DemoEquipment[] {
  const catById = Object.fromEntries(DEMO_CATEGORIES_DEF.map((c) => [c.id, c]));
  const locById = Object.fromEntries(DEMO_LOCATIONS_DEF.map((l) => [l.id, l]));

  return TEMPLATES.map((t, i) => {
    const seq = i + 1;
    const cat = catById[t.cat];
    const loc = locById[t.loc];
    const purchaseAgeDays = 120 + i * 47; // spread purchases over a couple of years
    const checkedOut = !!t.checkedOutTo;
    const status = t.status ?? 'GREEN';

    return {
      id: `eq_${pad(seq)}`,
      schoolId: DEMO_SCHOOL.id,
      name: t.name,
      description: `${t.brand}${t.model ? ` ${t.model}` : ''} — ${cat.name.toLowerCase()} equipment for the Rapids athletics program.`,
      brand: t.brand,
      model: t.model,
      serialNumber: `${t.brand.slice(0, 3).toUpperCase()}-${pad(1000 + seq)}`,
      qrCode: `RHS-${pad(seq)}`,
      categoryId: t.cat,
      category: cat.name,
      locationId: t.loc,
      location: loc.name,
      status,
      condition: t.condition,
      size: t.size,
      purchasePrice: t.price,
      cost: t.price.toFixed(2),
      purchaseDate: daysAgo(purchaseAgeDays),
      warrantyExpiry: daysFromNow(365 - (i % 5) * 60),
      lastInspection: daysAgo(30 + (i % 6) * 10),
      nextInspection: daysFromNow(60 - (i % 6) * 5),
      assignedTo: checkedOut ? t.checkedOutTo : undefined,
      checkedOutTo: checkedOut ? t.checkedOutTo : undefined,
      checkedOutDate: checkedOut ? daysAgo(t.dueInDays != null ? Math.max(1, 14 - t.dueInDays) : 7) : undefined,
      dueDate: checkedOut && t.dueInDays != null ? daysFromNow(t.dueInDays) : undefined,
      notes:
        status === 'RED'
          ? 'Flagged for repair — pulled from rotation until serviced.'
          : status === 'YELLOW'
            ? 'Showing wear; schedule inspection soon.'
            : status === 'LOST'
              ? 'Reported missing after last away meet.'
              : status === 'RETIRED'
                ? 'Retired from active inventory.'
                : 'In good working order.',
      images: [],
      tags: [cat.sport, t.brand],
      createdAt: daysAgo(purchaseAgeDays),
      updatedAt: daysAgo(i % 14),
    };
  });
}

export interface DemoTransaction {
  id: string;
  schoolId: string;
  equipmentId: string;
  equipmentName: string;
  equipmentCode: string;
  userId: string;
  userName: string;
  type: string; // TransactionType
  status: string;
  notes?: string;
  previousStatus?: string;
  newStatus?: string;
  fromLocationId?: string;
  toLocationId?: string;
  createdAt: string;
}

function buildTransactions(equipment: DemoEquipment[]): DemoTransaction[] {
  const txns: DemoTransaction[] = [];
  let n = 0;
  const staffName = (u: { firstName: string; lastName: string }) => `${u.firstName} ${u.lastName}`;
  const pushTxn = (eq: DemoEquipment, type: string, ageDays: number, opts: Partial<DemoTransaction> = {}) => {
    n += 1;
    const actor = DEMO_STAFF[(n + 1) % DEMO_STAFF.length];
    txns.push({
      id: `txn_${pad(n)}`,
      schoolId: DEMO_SCHOOL.id,
      equipmentId: eq.id,
      equipmentName: eq.name,
      equipmentCode: eq.qrCode,
      userId: actor.id,
      userName: opts.userName ?? staffName(actor),
      type,
      status: 'COMPLETED',
      createdAt: daysAgo(ageDays),
      ...opts,
    });
  };

  // Checkouts for currently-out items
  equipment
    .filter((e) => e.checkedOutTo)
    .forEach((e, idx) => pushTxn(e, 'CHECK_OUT', 6 + idx, { userName: e.checkedOutTo, notes: `Checked out to ${e.checkedOutTo}` }));

  // A history of completed check-out / check-in cycles on available items
  equipment
    .filter((e) => !e.checkedOutTo && e.status === 'GREEN')
    .slice(0, 10)
    .forEach((e, idx) => {
      pushTxn(e, 'CHECK_OUT', 40 - idx * 2, { userName: ATHLETES[idx % ATHLETES.length] });
      pushTxn(e, 'CHECK_IN', 33 - idx * 2, { userName: ATHLETES[idx % ATHLETES.length] });
    });

  // Maintenance + status changes on flagged items
  equipment
    .filter((e) => e.status === 'RED' || e.status === 'YELLOW')
    .forEach((e, idx) =>
      pushTxn(e, e.status === 'RED' ? 'MAINTENANCE' : 'INSPECTION', 4 + idx, {
        notes: e.status === 'RED' ? 'Sent for repair.' : 'Routine inspection logged.',
        previousStatus: 'GREEN',
        newStatus: e.status,
      }),
    );

  return txns.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export interface DemoState {
  organization: typeof DEMO_ORG;
  school: typeof DEMO_SCHOOL;
  user: typeof DEMO_USER;
  staff: typeof DEMO_STAFF;
  categories: Array<CategoryDef & { schoolId: string; requiresSize: boolean; requiresAssignment: boolean; equipmentCount: number }>;
  locations: Array<LocationDef & { schoolId: string; isSecure: boolean; equipmentCount: number }>;
  equipment: DemoEquipment[];
  transactions: DemoTransaction[];
}

/** Build a fresh, deep copy of the demo dataset (called on each page load). */
export function createDemoState(): DemoState {
  const equipment = buildEquipment();
  const transactions = buildTransactions(equipment);

  const categories = DEMO_CATEGORIES_DEF.map((c) => ({
    ...c,
    schoolId: DEMO_SCHOOL.id,
    requiresSize: ['cat_football', 'cat_baseball', 'cat_uniforms'].includes(c.id),
    requiresAssignment: ['cat_football', 'cat_uniforms'].includes(c.id),
    equipmentCount: equipment.filter((e) => e.categoryId === c.id).length,
  }));

  const locations = DEMO_LOCATIONS_DEF.map((l) => ({
    ...l,
    schoolId: DEMO_SCHOOL.id,
    isSecure: !!l.isSecure,
    equipmentCount: equipment.filter((e) => e.locationId === l.id).length,
  }));

  return {
    organization: DEMO_ORG,
    school: DEMO_SCHOOL,
    user: DEMO_USER,
    staff: DEMO_STAFF,
    categories,
    locations,
    equipment,
    transactions,
  };
}

export const DEMO_TOKEN = 'demo-access-token';
export const DEMO_REFRESH_TOKEN = 'demo-refresh-token';
